/* Is this cell in a fight right now?

   The client's half of `simulator/use_cases/under_siege.py`, and it exists for the same reason
   that file does: "in combat" must not come to mean three different things in three screens. The
   server is the gate — nothing here refuses anything — but a besieged city produces nothing
   (owner, 2026-09-08), and a food projection that keeps counting a stormed farm's harvest is a
   number on screen making a claim the server will not honour.

   Two shapes count, exactly as the server counts them. A battle: more than one team with units
   present, garrisons and stacks at the gates alike. And an *overrun* city: the owner's last
   defender is dead and only the enemy stands on it, which is more of a siege rather than less —
   ownership has not changed hands yet, so the owner still answers for what the cell may do.

   There used to be a third clause here — an infiltration was not a siege, having attacked nobody
   — and it went with infiltration itself on 2026-09-10. Every stack standing on a cell counts now,
   which is what the server's `teams_present` says too. */

/* Coordinates are "<col>-<row>", column first, everywhere — the metadata dict's keys, the canvas,
   the wire — and the browser sends them as strings. */
const keyOf = ({ column, row }) => `${column}-${row}`
const parseKey = (key) => {
  const [column, row] = String(key).split("-").map(Number)
  return { column, row }
}
const asCoords = (coords) =>
  typeof coords === "string"
    ? parseKey(coords)
    : { column: Number(coords?.column ?? coords?.col), row: Number(coords?.row) }

const bodiesOf = (movement) =>
  Object.values(movement?.units || {}).reduce((total, unit) => total + (unit?.number ?? 0), 0)

/* Where a stack stands this round. `path[current_index]` is a real position every tick, and since
   `forget_the_road_behind` the index is 0 between rounds — so read the position, never the index. */
export const stackPosition = (movement) => {
  const path = movement?.path || []
  if (!path.length) return null
  const [column, row] = path[Math.min(movement.current_index ?? 0, path.length - 1)]
  return { column: Number(column), row: Number(row) }
}

/* The teams with a live presence on `coords` — `{column, row}` or a "col-row" key. */
export const teamsPresentAt = ({ coords, cells, users, movements = [] }) => {
  const target = asCoords(coords)
  const teams = new Set()

  const cell = (cells || {})[keyOf(target)]
  Object.entries(cell?.metadata_by_user || {}).forEach(([id, metadata]) => {
    if (!Object.values(metadata?.units || {}).some((unit) => (unit?.number ?? 0) > 0)) return
    const team = users?.[String(id)]?.team
    if (team) teams.add(team)
  })

  ;(movements || []).forEach((movement) => {
    if (bodiesOf(movement) <= 0) return
    const at = stackPosition(movement)
    if (!at || at.column !== target.column || at.row !== target.row) return
    const team = users?.[String(movement.user_id)]?.team
    if (team) teams.add(team)
  })

  return teams
}

export const isUnderSiege = ({ coords, cells, users, movements = [] }) => {
  const teams = teamsPresentAt({ coords, cells, users, movements })
  if (teams.size > 1) return true
  if (teams.size === 0) return false

  const target = asCoords(coords)
  const cell = (cells || {})[keyOf(target)]
  if (cell?.owner == null) return false
  const ownerTeam = users?.[String(cell.owner)]?.team
  return Boolean(ownerTeam) && !teams.has(ownerTeam)
}

export default isUnderSiege
